import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { CalendarDays, Loader2, ExternalLink, X, FileBarChart } from "lucide-react";
import { patientApi } from "../services/api";
import { toast } from "sonner";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return "";
  }
};

const fmtRo = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}.${m}.${y}`;
};

// Toggle filtru tri-state: null (toți) → "cu" → "fara" → null
const FilterToggle = ({ label, value, onChange, testIdPrefix, showProprii = false }) => (
  <div className="inline-flex rounded-md border overflow-hidden text-xs" data-testid={`${testIdPrefix}-toggle`}>
    <span className="px-2 py-1 bg-muted text-muted-foreground uppercase font-medium">{label}</span>
    <button
      type="button"
      onClick={() => onChange(value === "all" ? "all" : "all")}
      className={`px-2 py-1 border-l transition ${value === "all" ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted"}`}
      data-testid={`${testIdPrefix}-all`}
    >
      Toți
    </button>
    <button
      type="button"
      onClick={() => onChange(value === "cu" ? "all" : "cu")}
      className={`px-2 py-1 border-l transition ${value === "cu" ? "bg-emerald-600 text-white" : "bg-white hover:bg-emerald-50 text-emerald-700"}`}
      data-testid={`${testIdPrefix}-cu`}
    >
      CU
    </button>
    <button
      type="button"
      onClick={() => onChange(value === "fara" ? "all" : "fara")}
      className={`px-2 py-1 border-l transition ${value === "fara" ? "bg-red-600 text-white" : "bg-white hover:bg-red-50 text-red-700"}`}
      data-testid={`${testIdPrefix}-fara`}
    >
      FĂRĂ
    </button>
    {showProprii && (
      <button
        type="button"
        onClick={() => onChange(value === "proprii" ? "all" : "proprii")}
        className={`px-2 py-1 border-l transition ${value === "proprii" ? "bg-violet-600 text-white" : "bg-white hover:bg-violet-50 text-violet-700"}`}
        data-testid={`${testIdPrefix}-proprii`}
      >
        PROPRII
      </button>
    )}
  </div>
);

// Selector compact pentru filtre single-value (Antrenor / Club / Sport). Folosește
// native <select> pentru un footprint mic; se dezactivează dacă nu există opțiuni.
const SelectFilter = ({ label, value, onChange, options, testId }) => {
  const disabled = !options || options.length === 0;
  return (
    <div className="inline-flex items-stretch rounded-md border overflow-hidden text-xs" data-testid={`${testId}-wrap`}>
      <span className="px-2 py-1 bg-muted text-muted-foreground uppercase font-medium flex items-center">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`px-2 py-1 border-l bg-white text-xs outline-none focus:ring-1 focus:ring-primary ${value !== "all" ? "font-semibold text-primary" : "text-slate-700"} disabled:opacity-50`}
        data-testid={testId}
      >
        <option value="all">Toți</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
};

export default function RaportZileiDialog({ open, onOpenChange }) {
  const [date, setDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ count: 0, patients: [] });
  const [filterEkg, setFilterEkg] = useState("all");        // all / cu / fara
  const [filterAnalize, setFilterAnalize] = useState("all"); // all / cu / fara / proprii
  const [filterCat, setFilterCat] = useState("all");         // all / juniori / seniori
  const [filterAntrenor, setFilterAntrenor] = useState("all");
  const [filterClub, setFilterClub] = useState("all");
  const [filterSport, setFilterSport] = useState("all");

  const fetchDaily = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await patientApi.dailyReport(d);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la generarea raportului.");
      setData({ count: 0, patients: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setDate(todayISO());
    setFilterEkg("all");
    setFilterAnalize("all");
    setFilterCat("all");
    setFilterAntrenor("all");
    setFilterClub("all");
    setFilterSport("all");
    fetchDaily(todayISO());
  }, [open, fetchDaily]);

  useEffect(() => {
    if (!open) return;
    fetchDaily(date);
  }, [date, open, fetchDaily]);

  const resetFilters = () => {
    setFilterEkg("all");
    setFilterAnalize("all");
    setFilterCat("all");
    setFilterAntrenor("all");
    setFilterClub("all");
    setFilterSport("all");
  };

  // Opțiuni unice pentru selectoarele Antrenor / Club / Sport (calculate din pacienții zilei)
  const uniqOptions = useMemo(() => {
    const antr = new Set();
    const club = new Set();
    const sport = new Set();
    (data.patients || []).forEach((p) => {
      const is = p.info_sportive || {};
      if (is.antrenor && String(is.antrenor).trim()) antr.add(String(is.antrenor).trim());
      if (is.club_sportiv && String(is.club_sportiv).trim()) club.add(String(is.club_sportiv).trim());
      if (is.sport_practicat && String(is.sport_practicat).trim()) sport.add(String(is.sport_practicat).trim());
    });
    const sortRo = (a, b) => a.localeCompare(b, "ro", { sensitivity: "base" });
    return {
      antrenori: Array.from(antr).sort(sortRo),
      cluburi: Array.from(club).sort(sortRo),
      sporturi: Array.from(sport).sort(sortRo),
    };
  }, [data]);

  const filtered = useMemo(() => {
    return (data.patients || []).filter((p) => {
      if (filterEkg !== "all" && (p.flag_ekg || "") !== filterEkg) return false;
      if (filterAnalize !== "all" && (p.flag_analize || "") !== filterAnalize) return false;
      const dp = p.date_personale || {};
      if (filterCat === "juniori" && !dp.juniori) return false;
      if (filterCat === "seniori" && !dp.seniori) return false;
      const is = p.info_sportive || {};
      if (filterAntrenor !== "all" && (is.antrenor || "").trim() !== filterAntrenor) return false;
      if (filterClub !== "all" && (is.club_sportiv || "").trim() !== filterClub) return false;
      if (filterSport !== "all" && (is.sport_practicat || "").trim() !== filterSport) return false;
      return true;
    });
  }, [data, filterEkg, filterAnalize, filterCat, filterAntrenor, filterClub, filterSport]);

  const hasActiveFilter =
    filterEkg !== "all" ||
    filterAnalize !== "all" ||
    filterCat !== "all" ||
    filterAntrenor !== "all" ||
    filterClub !== "all" ||
    filterSport !== "all";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="raport-zilei-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileBarChart className="w-5 h-5 text-primary" />
            Raportul zilei Înscrieri
          </DialogTitle>
          <DialogDescription>
            Vezi câți sportivi s-au înregistrat prin formular într-o anumită zi și filtrează după categorie / flag-uri.
          </DialogDescription>
        </DialogHeader>

        {/* Date picker */}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <Label htmlFor="raport-date" className="text-xs uppercase tracking-wide text-muted-foreground">Selectează data</Label>
            <div className="relative">
              <CalendarDays className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
              <Input
                id="raport-date"
                type="date"
                value={date}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                className="pl-9 w-52"
                data-testid="raport-date-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm font-mono" data-testid="raport-total-count">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : `${data.count} înregistrări`}
            </Badge>
            {hasActiveFilter && (
              <Badge variant="outline" className="text-xs" data-testid="raport-filtered-count">
                {filtered.length} după filtre
              </Badge>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-lg border p-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Filtre</div>
            {hasActiveFilter && (
              <Button size="sm" variant="ghost" className="h-6 text-xs gap-1" onClick={resetFilters} data-testid="raport-reset-filters">
                <X className="w-3 h-3" /> Reset
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <FilterToggle label="EKG" value={filterEkg} onChange={setFilterEkg} testIdPrefix="raport-filter-ekg" />
            <FilterToggle label="Analize" value={filterAnalize} onChange={setFilterAnalize} testIdPrefix="raport-filter-analize" showProprii />
            <div className="inline-flex rounded-md border overflow-hidden text-xs" data-testid="raport-filter-cat-toggle">
              <span className="px-2 py-1 bg-muted text-muted-foreground uppercase font-medium">Categorie</span>
              <button
                type="button"
                onClick={() => setFilterCat("all")}
                className={`px-2 py-1 border-l transition ${filterCat === "all" ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted"}`}
                data-testid="raport-filter-cat-all"
              >
                Toți
              </button>
              <button
                type="button"
                onClick={() => setFilterCat(filterCat === "juniori" ? "all" : "juniori")}
                className={`px-2 py-1 border-l transition ${filterCat === "juniori" ? "bg-blue-600 text-white" : "bg-white hover:bg-blue-50 text-blue-700"}`}
                data-testid="raport-filter-cat-juniori"
              >
                Juniori
              </button>
              <button
                type="button"
                onClick={() => setFilterCat(filterCat === "seniori" ? "all" : "seniori")}
                className={`px-2 py-1 border-l transition ${filterCat === "seniori" ? "bg-purple-600 text-white" : "bg-white hover:bg-purple-50 text-purple-700"}`}
                data-testid="raport-filter-cat-seniori"
              >
                Seniori
              </button>
            </div>

            {/* Selectoare: Antrenor / Club / Sport */}
            <SelectFilter
              label="Antrenor"
              value={filterAntrenor}
              onChange={setFilterAntrenor}
              options={uniqOptions.antrenori}
              testId="raport-filter-antrenor"
            />
            <SelectFilter
              label="Club"
              value={filterClub}
              onChange={setFilterClub}
              options={uniqOptions.cluburi}
              testId="raport-filter-club"
            />
            <SelectFilter
              label="Sport"
              value={filterSport}
              onChange={setFilterSport}
              options={uniqOptions.sporturi}
              testId="raport-filter-sport"
            />
          </div>
        </div>

        {/* Patient list */}
        <div className="rounded-lg border">
          <div className="px-3 py-2 border-b bg-muted/50 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center justify-between">
            <span>Sportivi înregistrați pe {fmtRo(date) || "—"}</span>
            <span data-testid="raport-visible-count">{filtered.length} vizibili</span>
          </div>
          <div className="max-h-[46vh] overflow-y-auto divide-y">
            {loading && (
              <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...
              </div>
            )}
            {!loading && data.patients.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground italic text-center" data-testid="raport-empty">
                Niciun sportiv înregistrat în această zi.
              </div>
            )}
            {!loading && data.patients.length > 0 && filtered.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground italic text-center" data-testid="raport-no-match">
                Niciun sportiv nu se potrivește cu filtrele curente.
              </div>
            )}
            {!loading && filtered.map((p) => {
              const dp = p.date_personale || {};
              const is = p.info_sportive || {};
              return (
                <div key={p.id} className="px-3 py-2.5 flex items-center gap-3 text-sm" data-testid={`raport-row-${p.id}`}>
                  <span className="text-muted-foreground w-10 shrink-0 text-xs">#{p.numar_ordine}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {dp.nume} {dp.prenume}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {is.sport_practicat || "—"} · {is.club_sportiv || "—"}
                      {is.antrenor ? ` · Antrenor: ${is.antrenor}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                    {p.is_returning && (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-amber-500 text-amber-800 bg-amber-50"
                        title="Sportiv existent în baza de date — a completat din nou formularul astăzi"
                        data-testid={`raport-returning-${p.id}`}
                      >
                        REVENIT
                      </Badge>
                    )}
                    {dp.juniori && <Badge variant="outline" className="text-[10px] border-blue-500 text-blue-700">Junior</Badge>}
                    {dp.seniori && <Badge variant="outline" className="text-[10px] border-purple-500 text-purple-700">Senior</Badge>}
                    {p.flag_ekg === "cu" && <Badge variant="outline" className="text-[10px] border-emerald-500 text-emerald-800 bg-emerald-50">CU EKG</Badge>}
                    {p.flag_ekg === "fara" && <Badge variant="outline" className="text-[10px] border-red-500 text-red-800 bg-red-50">FĂRĂ EKG</Badge>}
                    {p.flag_analize === "cu" && <Badge variant="outline" className="text-[10px] border-emerald-500 text-emerald-800 bg-emerald-50">CU ANALIZE</Badge>}
                    {p.flag_analize === "fara" && <Badge variant="outline" className="text-[10px] border-red-500 text-red-800 bg-red-50">FĂRĂ ANALIZE</Badge>}
                    {p.flag_analize === "proprii" && <Badge variant="outline" className="text-[10px] border-violet-500 text-violet-800 bg-violet-50">ANALIZE PROPRII</Badge>}
                    <span className="text-[10px] text-muted-foreground tabular-nums ml-1" data-testid={`raport-time-${p.id}`}>
                      {fmtTime(p.submission_at || p.created_at)}
                    </span>
                    <Link to={`/admin/patients/${p.id}`} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="icon" className="h-6 w-6" title="Deschide detalii sportiv" data-testid={`raport-open-${p.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
